// Tickerback landing: scroll orchestration, the 3D hero, the time-travel
// diagram, the strategy shapes and the team scene.
import { formatUsd, formatDate, callDays } from './data.js';
import { SITE } from './site.config.js';
import { copy } from './i18n.js';
import { INST_CHAIN, FUNDS, INST_COPY } from './institutional.js';

const html = document.documentElement;
const lang = html.lang === 'en' ? 'en' : 'es';
const t = copy(lang);
const usd = (v, o = {}) => formatUsd(v, { ...o, lang });
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
// Springs advance in fixed 60 Hz steps, so they feel the same on 60 and 120 Hz screens.
const SPRING_STEP_MS = 1000 / 60;
const springClock = () => {
  let last = 0;
  let acc = 0;
  return {
    steps(now) {
      acc += last ? Math.min(100, now - last) : SPRING_STEP_MS;
      last = now;
      const n = Math.floor(acc / SPRING_STEP_MS);
      acc -= n * SPRING_STEP_MS;
      return Math.min(n, 6);
    },
    reset() {
      last = 0;
      acc = 0;
    },
  };
};
const webgl = (() => {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
})();
// The hero falls back to its static layout on its own (see styles.css).
if (!webgl) html.classList.add('no-webgl');
if (!webgl || reduced) html.classList.add('stage-static');
if (reduced) html.classList.add('reduced');

/* ------------------------------------------------ business facts (config) */
// Everything that depends on the business lives in site.config.js; empty
// values stay hidden instead of showing a placeholder.
document.querySelectorAll('[data-app-link]').forEach((a) => {
  a.href = SITE.appUrl + a.dataset.appLink;
});
if (SITE.blogUrl) document.querySelectorAll('[data-blog-link]').forEach((a) => (a.href = SITE.blogUrl));
if (SITE.demoStrategyUrl) {
  document.querySelectorAll('[data-demo-link]').forEach((a) => {
    a.href = SITE.demoStrategyUrl;
    // a link without its own demo label keeps the text it was written with
    if (!a.dataset.demoLabel) return;
    // keep an arrow the label already has
    const arrow = a.querySelector('.arrow');
    a.textContent = a.dataset.demoLabel;
    if (arrow) a.append(' ', arrow);
  });
}
if (SITE.contactEmail) {
  document.querySelectorAll('[data-contact-link]').forEach((a) => {
    a.href = `mailto:${SITE.contactEmail}`;
    a.hidden = false;
  });
}
const salesEmail = SITE.salesEmail || SITE.contactEmail;
if (salesEmail) {
  document.querySelectorAll('[data-sales-link]').forEach((a) => {
    a.href = `mailto:${salesEmail}?subject=${encodeURIComponent('Tickerback Enterprise')}`;
    a.classList.remove('is-pending');
    a.removeAttribute('aria-disabled');
  });
}
const { legalName, taxId, address } = SITE.company;
if (legalName) {
  const company = document.querySelector('[data-company]');
  company.textContent = t.company(SITE.name, legalName, taxId, address);
  company.hidden = false;
}
document.querySelectorAll('[data-year]').forEach((el) => (el.textContent = String(new Date().getFullYear())));
// yesterday's session in the reader's order (25/09/26, 09/25/26): the data is updated every day
document.querySelectorAll('[data-yesterday]').forEach((el) => {
  const d = new Date(Date.now() - 86400000);
  const p2 = (n) => String(n).padStart(2, '0');
  const [dd, mm, yy] = [p2(d.getDate()), p2(d.getMonth() + 1), p2(d.getFullYear() % 100)];
  el.textContent = lang === 'en' ? `${mm}/${dd}/${yy}` : `${dd}/${mm}/${yy}`;
});
// copy that depends on a switch carries its other version in a data attribute
const switchCopy = (attr) =>
  document.querySelectorAll(`[${attr}]`).forEach((el) => (el.innerHTML = el.getAttribute(attr)));
if (!SITE.paidPlansOpen) {
  document.querySelector('[data-plans]').classList.remove('is-open');
  document.querySelectorAll('[data-soon]').forEach((b) => (b.hidden = false));
  switchCopy('data-when-closed');
  document.querySelectorAll('[data-paid-cta]').forEach((a) => {
    a.textContent = a.dataset.closedLabel;
    a.classList.replace('btn-primary', 'btn-glass');
  });
}
if (SITE.storeAppsPublished) switchCopy('data-when-apps');

const { gsap, ScrollTrigger, Lenis } = window;
gsap.registerPlugin(ScrollTrigger);
const stagePinned = () => !html.classList.contains('stage-static');

/* --------------------------------------------------------- smooth scroll */
let lenis = null;
if (!reduced && typeof Lenis === 'function') {
  lenis = new Lenis({ lerp: 0.09, smoothWheel: true, wheelMultiplier: 0.9 });
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);
}

const navHeight = () => document.querySelector('[data-nav]').offsetHeight;
// Move keyboard focus with the scroll, so Tab continues from the section.
function focusTarget(target, tries = 60) {
  if (!target.matches('a, button, input, select, textarea, [tabindex]')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  // a pinned section can still be fading in when the scroll ends; retry
  if (document.activeElement !== target && tries > 0) requestAnimationFrame(() => focusTarget(target, tries - 1));
}
function scrollToY(y, target) {
  if (lenis) lenis.scrollTo(y, { duration: 1.3, onComplete: () => focusTarget(target) });
  else {
    scrollTo({ top: y, behavior: reduced ? 'auto' : 'smooth' });
    focusTarget(target);
  }
}
document.querySelectorAll('a[href^="#"]').forEach((a) => {
  a.addEventListener('click', (e) => {
    const id = a.getAttribute('href');
    const target = id.length > 1 ? document.querySelector(id) : null;
    if (!target) return;
    e.preventDefault();
    const y = target.getBoundingClientRect().top + scrollY - (target.matches('main') ? 0 : navHeight());
    // floor: rounding up would tuck the heading a fraction under the nav
    scrollToY(Math.max(0, Math.floor(y)), target);
  });
});

/* ------------------------------------------------------------------ nav */
const nav = document.querySelector('[data-nav]');
const updateNav = () => nav.classList.toggle('is-scrolled', scrollY > 40);
addEventListener('scroll', updateNav, { passive: true });
updateNav();

/* --------------------------------------------------- accessible groups */
// A thumb slides under the selected option of a segmented control.
function updateThumb(container) {
  const thumb = container.querySelector(':scope > .seg-thumb');
  const active = container.querySelector('[aria-selected="true"], [aria-checked="true"]');
  if (!thumb || !active) return;
  thumb.style.width = `${active.offsetWidth}px`;
  thumb.style.height = `${active.offsetHeight}px`;
  thumb.style.transform = `translate(${active.offsetLeft}px, ${active.offsetTop}px)`;
}
function addThumb(container) {
  const thumb = document.createElement('span');
  thumb.className = 'seg-thumb';
  thumb.setAttribute('aria-hidden', 'true');
  thumb.style.transition = 'none';
  container.prepend(thumb);
  container.classList.add('has-thumb');
  updateThumb(container);
  requestAnimationFrame(() => requestAnimationFrame(() => (thumb.style.transition = '')));
  if ('ResizeObserver' in window) new ResizeObserver(() => updateThumb(container)).observe(container);
}

// Segmented controls behave as native radio groups (or tabs): roving
// tabindex, arrow keys and Home/End move and select.
function rovingGroup(container, role, onSelect) {
  const items = [...container.querySelectorAll(`[role="${role}"]`)];
  const attr = role === 'tab' ? 'aria-selected' : 'aria-checked';
  const sync = (active) => {
    items.forEach((r) => {
      r.setAttribute(attr, String(r === active));
      r.tabIndex = r === active ? 0 : -1;
    });
    updateThumb(container);
  };
  const select = (item, user = true) => {
    sync(item);
    onSelect(item, user);
  };
  items.forEach((r) => r.addEventListener('click', () => select(r)));
  container.addEventListener('keydown', (e) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1, Home: 'first', End: 'last' };
    if (!(e.key in keys)) return;
    e.preventDefault();
    const current = items.indexOf(document.activeElement);
    const from = current >= 0 ? current : items.findIndex((r) => r.getAttribute(attr) === 'true');
    const step = keys[e.key];
    const index = step === 'first' ? 0 : step === 'last' ? items.length - 1 : (from + step + items.length) % items.length;
    items[index].focus();
    select(items[index]);
  });
  sync(items.find((r) => r.getAttribute(attr) === 'true') || items[0]);
  return { items, select, sync };
}

/* ------------------------------------------------------------- odometer */
// The market date rewinds through real calendar days from the visitor's
// today to data-to (an ISO date), written in the reader's order: 05/01/24 in
// Spanish, 01/05/24 in English. Each digit rolls to its new value.
const odo = document.querySelector('[data-odometer]');
const now = new Date();
const odoFromMs = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
const odoToMs = Date.parse(`${odo.dataset.to}T00:00:00Z`);
const fmtYmd = (ms) => {
  const d = new Date(ms);
  const p2 = (n) => String(n).padStart(2, '0');
  const [dd, mm, yy] = [p2(d.getUTCDate()), p2(d.getUTCMonth() + 1), p2(d.getUTCFullYear() % 100)];
  return lang === 'en' ? `${mm}/${dd}/${yy}` : `${dd}/${mm}/${yy}`;
};
const strips = [];
for (const ch of fmtYmd(odoFromMs)) {
  if (ch === '/') {
    const s = document.createElement('span');
    s.className = 'sep';
    s.textContent = '/';
    odo.appendChild(s);
    strips.push(null);
    continue;
  }
  const d = document.createElement('span');
  d.className = 'digit';
  const strip = document.createElement('span');
  strip.className = 'strip';
  for (let k = 0; k < 10; k++) {
    const n = document.createElement('span');
    n.textContent = String(k);
    strip.appendChild(n);
  }
  d.appendChild(strip);
  odo.appendChild(d);
  strips.push(strip);
}
odo.setAttribute('aria-label', `${t.marketDate} ${fmtYmd(odoToMs)}`);
let lastOdo = '';
function setOdometer(p) {
  const eased = gsap.parseEase('power2.inOut')(gsap.utils.clamp(0, 1, p));
  const day = 86400000;
  const ms = Math.round((odoFromMs + (odoToMs - odoFromMs) * eased) / day) * day;
  const text = fmtYmd(ms);
  if (text === lastOdo) return;
  lastOdo = text;
  for (let i = 0; i < text.length; i++) {
    if (strips[i]) strips[i].style.transform = `translateY(${-Number(text[i])}em)`;
  }
}
setOdometer(0);

/* ---------------------------------------------------------------- stage */
const stageEl = document.querySelector('[data-stage]');
const stageState = { lid: 0, glow: 0, push: 0, steps: 0, screen: 0 };
const heroCopy = document.querySelector('[data-hero-copy]');
const rewind = document.querySelector('[data-rewind]');
const rewindCaption = document.querySelector('[data-rewind-caption]');
const quote = document.querySelector('[data-stage-quote]');
const steps = document.querySelector('[data-steps]');
const stepItems = [...document.querySelectorAll('[data-step]')];
let stage3d = null;
// the app in the page's language on the laptop's screen
const STAGE_SCREENS = {
  es: ['assets/captures/web-lesson.webp', 'assets/captures/web-chain-call.webp', 'assets/captures/web-strategy.webp'],
  en: ['assets/captures/web-lesson-en.webp', 'assets/captures/web-chain-call-en.webp', 'assets/captures/web-strategy-en.webp'],
};

function setActiveStep(i) {
  stepItems.forEach((li, k) => li.classList.toggle('is-active', k === i));
}
setActiveStep(0);

if (stagePinned()) {
  const odoProxy = { p: 0 };
  const tl = gsap.timeline({
    defaults: { ease: 'none' },
    scrollTrigger: { trigger: stageEl, start: 'top top', end: 'bottom bottom', scrub: 0.7 },
    onUpdate: () => {
      stage3d?.update(stageState);
      const s = stageState.screen;
      setActiveStep(s < 0.5 ? 0 : s < 1.5 ? 1 : 2);
    },
  });
  // 0.00-0.28 the lid opens and the headline leaves
  tl.to(heroCopy, { autoAlpha: 0, y: -60, duration: 0.12, ease: 'power1.in' }, 0.05)
    .to(stageState, { lid: 1, duration: 0.24, ease: 'power2.inOut' }, 0.03)
    .to(stageState, { glow: 1, duration: 0.1, ease: 'power1.inOut' }, 0.16)
    // 0.22-0.46 camera pushes in and the date rewinds
    .to(stageState, { push: 1, duration: 0.18, ease: 'power2.inOut' }, 0.22)
    .fromTo(rewind, { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.05, ease: 'power2.out' }, 0.23)
    .to(odoProxy, { p: 1, duration: 0.15, onUpdate: () => setOdometer(odoProxy.p) }, 0.25)
    .to(rewindCaption, { opacity: 1, duration: 0.04 }, 0.38)
    .fromTo(quote, { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.05, ease: 'power2.out' }, 0.36)
    // 0.48-0.56 hand-off to the three steps
    .to([rewind, quote], { autoAlpha: 0, y: -20, duration: 0.05, ease: 'power1.in' }, 0.48)
    .to(stageState, { steps: 1, duration: 0.1, ease: 'power2.inOut' }, 0.5)
    .fromTo(steps, { autoAlpha: 0, x: -30 }, { autoAlpha: 1, x: 0, duration: 0.06, ease: 'power2.out' }, 0.55)
    .to(stageState, { screen: 1, duration: 0.06, ease: 'power1.inOut' }, 0.68)
    .to(stageState, { screen: 2, duration: 0.06, ease: 'power1.inOut' }, 0.82)
    .to({}, { duration: 0.08 }, 0.92);

  import('./stage3d.js')
    .then(async ({ createStage }) => {
      stage3d = await createStage(document.querySelector('[data-stage-canvas]'), { screens: STAGE_SCREENS[lang] });
      stage3d.update(stageState);
      stage3d.observe(stageEl);
    })
    .catch((error) => {
      // The hero scene could not start: show the static layout instead of an
      // empty pinned stage.
      console.warn('Hero 3D scene unavailable, using the static layout.', error);
      tl.scrollTrigger?.kill();
      tl.kill();
      gsap.set([heroCopy, steps, rewind, quote, rewindCaption], { clearProps: 'all' });
      gsap.set('.hero-copy > *', { clearProps: 'all' });
      html.classList.add('stage-static');
      ScrollTrigger.refresh();
    });
}

// Entrance of the headline: the page's single load moment.
if (!reduced) {
  gsap.from('.hero-copy > *', { y: 32, autoAlpha: 0, duration: 1.1, stagger: 0.08, ease: 'expo.out', delay: 0.1 });
}

/* ------------------------------------------------ time-travel diagram */
const SVGNS = 'http://www.w3.org/2000/svg';
const svgEl = (tag, attrs = {}, text) => {
  const el = document.createElementNS(SVGNS, tag);
  for (const k in attrs) el.setAttribute(k, attrs[k]);
  if (text != null) el.textContent = text;
  return el;
};

/* ------------------------------------------------------------ team scene */
const team = document.querySelector('[data-team]');
if (team) {
  const photos = [...team.querySelectorAll('[data-team-photo]')];
  const tabs = team.querySelector('[role="tablist"]');
  let current = 0;
  let timer = null;
  let inView = false;
  let hovered = false;
  photos.forEach((p, i) => gsap.set(p, i === 0 ? { rotationY: 0, autoAlpha: 1, z: 0 } : { rotationY: 100, autoAlpha: 0, z: -140 }));

  const show = (next) => {
    if (next === current) return;
    const from = photos[current];
    const to = photos[next];
    const dir = next > current || (current === photos.length - 1 && next === 0) ? 1 : -1;
    if (reduced) {
      gsap.set(from, { autoAlpha: 0 });
      gsap.set(to, { autoAlpha: 1, rotationY: 0, z: 0 });
    } else {
      // a quick turn, as if the same strategy spun from one screen to the next
      gsap
        .timeline()
        .to(from, { rotationY: -100 * dir, z: -140, autoAlpha: 0, duration: 0.55, ease: 'power3.in' }, 0)
        .fromTo(to, { rotationY: 100 * dir, z: -140, autoAlpha: 0 }, { rotationY: 0, z: 0, autoAlpha: 1, duration: 0.7, ease: 'power3.out' }, 0.32);
    }
    current = next;
    group.sync(group.items[next]);
  };
  const group = rovingGroup(tabs, 'tab', (tab) => {
    const index = photos.findIndex((p) => p.dataset.teamPhoto === tab.dataset.deviceTab);
    show(index);
    restart();
  });
  const tick = () => show((current + 1) % photos.length);
  const restart = () => {
    clearInterval(timer);
    timer = null;
    if (!reduced && inView && !hovered) timer = setInterval(tick, 3200);
  };
  team.addEventListener('pointerenter', () => ((hovered = true), restart()));
  team.addEventListener('pointerleave', () => ((hovered = false), restart()));
  team.addEventListener('focusin', () => ((hovered = true), restart()));
  team.addEventListener('focusout', () => ((hovered = false), restart()));
  new IntersectionObserver(
    (entries) => {
      inView = entries.some((e) => e.isIntersecting);
      restart();
    },
    { threshold: 0.35 },
  ).observe(team.querySelector('.team-stage'));
}

/* ------------------------------------------------------------- billing */
const proPrice = document.querySelector('[data-pro-price]');
const proNote = document.querySelector('[data-pro-note]');
rovingGroup(document.querySelector('.billing-switch'), 'radio', (b) => {
  const yearly = b.dataset.billing === 'yearly';
  proPrice.innerHTML = t.price(yearly ? 49 : 59);
  proNote.innerHTML = yearly ? t.proYearly(usd(588)) : t.proMonthly;
});

/* ----------------------------------------------------- gentle motion */
// Content always rests in place; motion only adds depth as it scrolls by.
if (!reduced) {
  gsap.utils.toArray('.lift').forEach((el) => {
    gsap.fromTo(el, { y: 40, rotateX: 3, transformPerspective: 1400 }, {
      y: -20,
      rotateX: 0,
      ease: 'none',
      scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.6 },
    });
  });
  // full-bleed photos drift a little slower than the page
  gsap.utils.toArray('[data-parallax]').forEach((img) => {
    if (matchMedia('(max-width: 820px)').matches) return;
    gsap.fromTo(img, { yPercent: -5 }, {
      yPercent: 5,
      ease: 'none',
      scrollTrigger: { trigger: img.closest('section'), start: 'top bottom', end: 'bottom top', scrub: 0.5 },
    });
  });
  gsap.utils.toArray('.section-head').forEach((el) => {
    gsap.from(el, { y: 26, duration: 0.9, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 88%', once: true } });
  });
}

/* ------------------------------------------------- revive (particles) */
const revive = document.querySelector('[data-revive]');
if (revive) {
  const reviveSteps = [...revive.querySelectorAll('[data-revive-step]')];
  const setReviveStep = (k) =>
    reviveSteps.forEach((li, i) => {
      // re-adding the class restarts the progress line under the step
      li.classList.remove('is-active');
      if (i === k) {
        void li.offsetWidth;
        li.classList.add('is-active');
      }
      li.querySelector('button').setAttribute('aria-pressed', String(i === k));
    });
  if (webgl) {
    const nearRevive = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        nearRevive.disconnect();
        import('./particles.js')
          .then(({ createParticles }) =>
            createParticles(revive.querySelector('[data-revive-canvas]'), {
              reduced,
              lang,
              onStep: (k, ms) => {
                revive.classList.add('is-live');
                if (ms) revive.style.setProperty('--revive-dur', `${ms}ms`);
                setReviveStep(k);
              },
            }),
          )
          .then((particles) =>
            reviveSteps.forEach((li, i) => li.querySelector('button').addEventListener('click', () => particles.goTo(i))),
          )
          .catch((error) => {
            console.warn('Particles unavailable.', error);
            revive.classList.add('revive-static');
          });
      },
      { rootMargin: '700px 0px' },
    );
    nearRevive.observe(revive);
  } else {
    revive.classList.add('revive-static');
  }
}

/* -------------------------------------------------- the call, two ways */
// The 30 real sessions of the 215 call, drawn one after another; a marker on a
// spring follows the newest one and the pointer can inspect any day.
const story = document.querySelector('[data-story]');
if (story) {
  const svg = story.querySelector('[data-story-svg]');
  // the page carries a still drawing for readers without JavaScript
  svg.replaceChildren();
  const readout = story.querySelector('[data-story-readout]');
  const days = callDays();
  const W = 720;
  const H = 260;
  const pad = { l: 52, r: 16, t: 18, b: 34 };
  const X = (i) => pad.l + (i / (days.length - 1)) * (W - pad.l - pad.r);
  const Y = (p) => pad.t + ((780 - p) / 1400) * (H - pad.t - pad.b);
  const g = svgEl('g');
  svg.appendChild(g);
  for (const p of [-500, 0, 500]) {
    g.appendChild(svgEl('line', { x1: pad.l, x2: W - pad.r, y1: Y(p), y2: Y(p), stroke: p === 0 ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.08)', 'stroke-width': 1 }));
    g.appendChild(svgEl('text', { x: pad.l - 8, y: Y(p) + 4, 'text-anchor': 'end' }, p === 0 ? '0' : usd(p, { sign: true })));
  }
  [0, 9, 19, 29].forEach((i) => g.appendChild(svgEl('text', { x: X(i), y: H - 8, 'text-anchor': 'middle' }, formatDate(days[i].date, lang).replace(/ \d{4}$|, \d{4}$/, ''))));
  const defs = svgEl('defs');
  const grad = svgEl('linearGradient', { id: 'story-line', x1: 0, x2: 1, y1: 0, y2: 0 });
  grad.append(svgEl('stop', { offset: '0%', 'stop-color': '#7ce38b' }), svgEl('stop', { offset: '60%', 'stop-color': '#6fd6e8' }), svgEl('stop', { offset: '100%', 'stop-color': '#b7a6ff' }));
  const glow = svgEl('filter', { id: 'story-glow', x: '-20%', y: '-40%', width: '140%', height: '180%' });
  glow.append(svgEl('feGaussianBlur', { stdDeviation: 4, result: 'b' }));
  const merge = svgEl('feMerge');
  merge.append(svgEl('feMergeNode', { in: 'b' }), svgEl('feMergeNode', { in: 'SourceGraphic' }));
  glow.append(merge);
  defs.append(grad, glow);
  svg.appendChild(defs);
  const area = svgEl('path', { fill: 'rgba(124,227,139,0.08)' });
  const path = svgEl('path', { fill: 'none', stroke: 'url(#story-line)', 'stroke-width': 3, 'stroke-linejoin': 'round', 'stroke-linecap': 'round', filter: 'url(#story-glow)' });
  const dots = svgEl('g');
  const marker = svgEl('circle', { r: 6.5, fill: '#fff', stroke: '#6fd6e8', 'stroke-width': 3 });
  const guide = svgEl('line', { y1: pad.t, y2: H - pad.b, stroke: 'rgba(255,255,255,0.22)', 'stroke-width': 1 });
  svg.append(area, path, dots, guide, marker);
  days.forEach((d, i) => dots.appendChild(svgEl('circle', { cx: X(i), cy: Y(d.pnl), r: 2.6, fill: d.pnl >= 0 ? '#7ce38b' : '#ff7d91', opacity: 0 })));
  const say = (i) => {
    const d = days[i];
    readout.textContent = `${formatDate(d.date, lang)} · AAPL ${usd(d.close, { decimals: 2 })} · ${usd(Math.round(d.pnl), { sign: true })}`;
  };
  let shown = -1;
  const drawTo = (n) => {
    shown = n;
    if (n < 0) {
      path.removeAttribute('d');
      area.removeAttribute('d');
      return;
    }
    let d = '';
    for (let i = 0; i <= n; i++) d += `${i ? 'L' : 'M'}${X(i).toFixed(1)},${Y(days[i].pnl).toFixed(1)}`;
    path.setAttribute('d', d);
    area.setAttribute('d', `${d}L${X(n)},${Y(0)}L${X(0)},${Y(0)}Z`);
    [...dots.children].forEach((c, i) => c.setAttribute('opacity', i <= n ? 1 : 0));
  };
  // marker on a spring
  let mx = X(0);
  let my = Y(0);
  let vx = 0;
  let vy = 0;
  let tx = mx;
  let ty = my;
  let springing = 0;
  const markerClock = springClock();
  const place = () => {
    marker.setAttribute('cx', mx.toFixed(1));
    marker.setAttribute('cy', my.toFixed(1));
    guide.setAttribute('x1', mx.toFixed(1));
    guide.setAttribute('x2', mx.toFixed(1));
  };
  const spring = (now) => {
    for (let n = markerClock.steps(now); n > 0; n--) {
      vx = (vx + (tx - mx) * 0.14) * 0.72;
      vy = (vy + (ty - my) * 0.14) * 0.72;
      mx += vx;
      my += vy;
    }
    place();
    springing = Math.abs(vx) + Math.abs(vy) + Math.abs(tx - mx) + Math.abs(ty - my) > 0.05 ? requestAnimationFrame(spring) : 0;
    if (!springing) markerClock.reset();
  };
  const aim = (i) => {
    tx = X(i);
    ty = Y(days[i].pnl);
    marker.setAttribute('stroke', days[i].pnl >= 0 ? '#6fd6e8' : '#ff7d91');
    if (reduced) {
      mx = tx;
      my = ty;
      place();
    } else if (!springing) springing = requestAnimationFrame(spring);
    say(i);
  };
  let playing = 0;
  const play = () => {
    clearInterval(playing);
    if (reduced) {
      drawTo(days.length - 1);
      aim(days.length - 1);
      return;
    }
    let i = 0;
    drawTo(0);
    aim(0);
    playing = setInterval(() => {
      i += 1;
      drawTo(i);
      aim(i);
      if (i >= days.length - 1) clearInterval(playing);
    }, 170);
  };
  new IntersectionObserver((entries, io) => {
    if (entries.some((e) => e.isIntersecting)) {
      io.disconnect();
      play();
    }
  }, { threshold: 0.45 }).observe(svg);
  story.querySelector('[data-story-replay]').addEventListener('click', play);
  svg.addEventListener('pointermove', (e) => {
    if (shown < 0) return;
    const r = svg.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * W;
    const i = Math.max(0, Math.min(shown, Math.round(((x - pad.l) / (W - pad.l - pad.r)) * (days.length - 1))));
    aim(i);
  });
  svg.addEventListener('pointerleave', () => shown >= 0 && aim(shown));
  drawTo(-1);
}

/* ----------------------------------------------------------- 3D cards */
// cards lean towards the pointer and spring back when it leaves
if (finePointer && !reduced) {
  document.querySelectorAll('[data-tilt-card]').forEach((card) => {
    card.addEventListener('pointermove', (e) => {
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      card.classList.add('is-tilting');
      card.style.setProperty('--ry', `${(x * 9).toFixed(2)}deg`);
      card.style.setProperty('--rx', `${(-y * 7).toFixed(2)}deg`);
    });
    card.addEventListener('pointerleave', () => {
      card.classList.remove('is-tilting');
      card.style.setProperty('--rx', '0deg');
      card.style.setProperty('--ry', '0deg');
    });
  });
}

/* -------------------------------------------------- the six words */
// The glossary cards fall into place on springs when the section arrives; with
// a mouse they can be dragged and flung, and they spring back to their slot.
const terms = [...document.querySelectorAll('.glossary-grid .term')];
if (terms.length && !reduced) {
  const bodies = terms.map((el, i) => ({ el, x: 0, y: 0, r: 0, vx: 0, vy: 0, vr: 0, held: false, delay: i * 90, start: 0 }));
  let raf = 0;
  const clock = springClock();
  const step = (now) => {
    const n = clock.steps(now);
    let moving = false;
    for (const b of bodies) {
      if (b.start && now < b.start + b.delay) {
        moving = true;
        continue;
      }
      if (!b.held) {
        for (let k = 0; k < n; k++) {
          b.vx = (b.vx - b.x * 0.075) * 0.8;
          b.vy = (b.vy - b.y * 0.075) * 0.8;
          b.vr = (b.vr - b.r * 0.08) * 0.78;
          b.x += b.vx;
          b.y += b.vy;
          b.r += b.vr;
        }
      }
      if (b.held || Math.abs(b.x) + Math.abs(b.y) + Math.abs(b.vx) + Math.abs(b.vy) + Math.abs(b.r) > 0.05) moving = true;
      else b.x = b.y = b.r = 0;
      b.el.style.translate = `${b.x.toFixed(1)}px ${b.y.toFixed(1)}px`;
      b.el.style.rotate = `${b.r.toFixed(2)}deg`;
    }
    raf = moving ? requestAnimationFrame(step) : 0;
    if (!raf) clock.reset();
  };
  const wake = () => {
    if (!raf) raf = requestAnimationFrame(step);
  };
  // drop in once, as the section scrolls into view
  new IntersectionObserver((entries, io) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    io.disconnect();
    const now = performance.now();
    bodies.forEach((b, i) => {
      b.y = -260 - i * 30;
      b.r = (i % 2 ? 1 : -1) * (8 + i * 2);
      b.start = now;
    });
    wake();
  }, { threshold: 0.2 }).observe(terms[0].closest('.glossary-grid'));
  if (finePointer) {
    bodies.forEach((b) => {
      let lastX = 0;
      let lastY = 0;
      let lastT = 0;
      b.el.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        b.held = true;
        b.start = 0;
        lastX = e.clientX;
        lastY = e.clientY;
        lastT = e.timeStamp;
        b.el.setPointerCapture(e.pointerId);
        b.el.classList.add('is-held');
        wake();
      });
      b.el.addEventListener('pointermove', (e) => {
        if (!b.held) return;
        const dx = e.clientX - lastX;
        const dy = e.clientY - lastY;
        // the fling speed in px per spring step, whatever the pointer's event rate
        const per = SPRING_STEP_MS / Math.max(4, e.timeStamp - lastT);
        lastX = e.clientX;
        lastY = e.clientY;
        lastT = e.timeStamp;
        b.x += dx;
        b.y += dy;
        b.vx = Math.max(-60, Math.min(60, dx * per));
        b.vy = Math.max(-60, Math.min(60, dy * per));
        b.r = Math.max(-14, Math.min(14, b.r + dx * 0.25));
      });
      const release = () => {
        if (!b.held) return;
        b.held = false;
        b.el.classList.remove('is-held');
        wake();
      };
      b.el.addEventListener('pointerup', release);
      b.el.addEventListener('pointercancel', release);
    });
  }
}

/* ---------------------------------------------- institutional (13F) */
// The 13F callout of Features: pick a fund and read who it is, what it did
// with its shares, at what estimated price, and what that can tell you.
const instCallout = document.querySelector('[data-inst-callout]');
if (instCallout) {
  const ic = INST_COPY[lang] || INST_COPY.es;
  const num = (v, d = 2) => v.toLocaleString(lang === 'en' ? 'en-US' : 'es-ES', { minimumFractionDigits: d, maximumFractionDigits: d });
  const big = (n) => {
    const a = Math.abs(n);
    if (a >= 1e6) return `${num(a / 1e6, a >= 1e8 ? 1 : 2)} M`;
    return num(a, 0);
  };
  const strikeOf = (id) => INST_CHAIN.find((row) => row.funds.includes(id))?.k;
  const chips = document.createElement('div');
  chips.className = 'sc-inst-chips';
  chips.setAttribute('role', 'group');
  chips.setAttribute('aria-label', lang === 'en' ? '13F funds on the chain' : 'Fondos 13F en la cadena');
  const card = document.createElement('div');
  card.className = 'sc-inst-card';
  card.setAttribute('aria-live', 'polite');
  const buttons = ['BE', 'VI', 'BR', 'RE'].map((id) => {
    const f = FUNDS[id];
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = f.name;
    b.dataset.fund = id;
    b.setAttribute('aria-pressed', 'false');
    b.setAttribute('aria-label', ic.aria(f.name, strikeOf(id)));
    chips.append(b);
    return b;
  });
  const show = (b) => {
    const id = b.dataset.fund;
    const f = FUNDS[id];
    buttons.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    const buy = f.action === 'new' || f.action === 'increase';
    card.innerHTML = `
      <div class="sc-inst-name"><b>${f.legal}</b><small>Strike ${num(strikeOf(id), 0)}</small></div>
      <p class="sc-inst-action ${buy ? 'is-buy' : 'is-sell'}">${ic.actions[f.action]} · ${ic.quarter(f.q)}</p>
      <dl class="sc-inst-facts">
        <div><dt>${ic.shares}</dt><dd class="num">${f.shares > 0 ? '+' : '−'}${big(f.shares)}</dd></div>
        <div><dt>${ic.price}</dt><dd class="num">≈ ${usd(f.price, { decimals: 2 })}</dd></div>
        <div><dt>${ic.filed}</dt><dd class="num">${formatDate(f.filed, lang)}</dd></div>
      </dl>
      <p>${ic.clue(f.name)[f.action]}</p>
      <p class="sc-inst-note">${ic.disclaimer}</p>`;
  };
  buttons.forEach((b) => b.addEventListener('click', () => show(b)));
  instCallout.append(chips, card);
  show(buttons[0]);
}

/* ------------------------------------------------------------ features */
// The list picks the feature; the laptop (or, without WebGL, the capture)
// shows it. On a wide screen the showcase stays pinned while you scroll: each
// stretch of scroll brings the next feature and, after the last one, the page
// scrolls on. On smaller screens a tap picks it.
const showcase = document.querySelector('[data-showcase]');
if (showcase) {
  const tabs = [...showcase.querySelectorAll('[role="tab"]')];
  const stage = showcase.querySelector('[role="tabpanel"]');
  const callouts = [...showcase.querySelectorAll('[data-sc-callout]')];
  const fallback = showcase.querySelector('[data-sc-fallback]');
  const device = showcase.querySelector('[data-sc-device]');
  const ROOT = html.dataset.root || '';
  // From disk, a capture may exist only inline (the self-contained draft
  // written by tools/export_draft.mjs), as the 3D textures already assume.
  const captureSrc = (path) => (location.protocol === 'file:' && window.__TB_CAPTURES?.[path]) || ROOT + path;
  const FALLBACK = {
    chain: 'web-chain-call',
    strategy: 'web-strategy',
    inst: 'crop-13f',
    news: 'crop-news',
    lesson: 'web-lesson',
  };
  let index = 0;
  let laptop = null;
  let pin = null;

  const show = (i) => {
    index = i;
    const key = tabs[i].dataset.sc;
    tabs.forEach((t, k) => {
      const on = k === i;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      if (!on) t.style.setProperty('--fill', '0');
    });
    stage.setAttribute('aria-labelledby', tabs[i].id);
    callouts.forEach((c) => c.classList.toggle('is-on', c.dataset.scCallout === key));
    if (laptop) laptop.show(key);
    else if (fallback) fallback.src = captureSrc(`assets/captures/${FALLBACK[key]}${lang === 'en' ? '-en' : ''}.webp`);
  };

  // pinned on wide screens: scroll progress → feature, and its silver line fills
  const wide = matchMedia('(min-width: 981px) and (min-height: 620px)');
  const step = () => Math.max(320, innerHeight * 0.42);
  const setupPin = () => {
    pin?.kill(true);
    pin = null;
    showcase.classList.remove('is-pinned');
    if (reduced || !wide.matches || typeof ScrollTrigger === 'undefined') return;
    pin = ScrollTrigger.create({
      trigger: showcase,
      start: 'center center',
      end: () => `+=${Math.round(step() * tabs.length)}`,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        const x = self.progress * tabs.length;
        const i = Math.min(tabs.length - 1, Math.floor(x));
        if (i !== index) show(i);
        tabs[i].style.setProperty('--fill', String(Math.min(1, x - i)));
      },
    });
    showcase.classList.add('is-pinned');
  };
  setupPin();
  wide.addEventListener('change', () => {
    setupPin();
    ScrollTrigger.refresh();
  });

  // choosing a feature: while pinned, scroll to its stretch; otherwise show it
  const goTo = (i, { focus = false } = {}) => {
    const k = (i + tabs.length) % tabs.length;
    show(k);
    if (pin) {
      const y = Math.round(pin.start + ((k + 0.06) / tabs.length) * (pin.end - pin.start));
      if (lenis) lenis.scrollTo(y, { duration: 0.9 });
      else scrollTo({ top: y, behavior: 'smooth' });
    }
    if (focus) tabs[k].focus({ preventScroll: true });
  };
  tabs.forEach((t, k) => t.addEventListener('click', () => goTo(k)));
  // menu links to one feature (13F, news) open its tab
  showcase.parentElement.querySelectorAll('[data-sc-anchor]').forEach((anchor) => {
    const k = tabs.findIndex((t) => t.dataset.sc === anchor.dataset.scAnchor);
    document.querySelectorAll(`a[href="#${anchor.id}"]`).forEach((a) => a.addEventListener('click', () => goTo(k)));
    if (location.hash === `#${anchor.id}`) show(k);
  });
  showcase.querySelector('[role="tablist"]').addEventListener('keydown', (e) => {
    const move = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (move) goTo(index + move, { focus: true });
    else if (e.key === 'Home') goTo(0, { focus: true });
    else if (e.key === 'End') goTo(tabs.length - 1, { focus: true });
    else return;
    e.preventDefault();
  });

  // the 3D laptop loads when Features comes near; until then (and without
  // WebGL) the capture stands in
  if (webgl && device) {
    const near = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        near.disconnect();
        import('./showcase3d.js')
          .then(({ createShowcase }) => createShowcase(device, { lang, reduced, first: tabs[index].dataset.sc }))
          .then((api) => {
            laptop = api;
            laptop.show(tabs[index].dataset.sc);
          })
          .catch((error) => console.warn('The 3D laptop is unavailable, showing the captures.', error));
      },
      { rootMargin: '800px 0px' },
    );
    near.observe(showcase);
  }
}

/* ------------------------------------------------------ pointer effects */
if (finePointer) {
  // a soft light that follows the pointer over cards
  document.querySelectorAll('[data-spotlight]').forEach((card) => {
    card.addEventListener('pointermove', (e) => {
      const r = card.getBoundingClientRect();
      card.style.setProperty('--mx', `${e.clientX - r.left}px`);
      card.style.setProperty('--my', `${e.clientY - r.top}px`);
    });
  });

  // floating panels lean towards the pointer
  if (!reduced) {
    document.querySelectorAll('[data-tilt]').forEach((panel) => {
      const tile = panel.closest('[data-spotlight]') || panel;
      tile.addEventListener('pointermove', (e) => {
        const r = panel.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        panel.classList.add('is-tilting');
        panel.style.setProperty('--ry', `${(x * 10).toFixed(2)}deg`);
        panel.style.setProperty('--rx', `${(-y * 8).toFixed(2)}deg`);
        panel.style.setProperty('--gx', `${((x + 0.5) * 100).toFixed(1)}%`);
        panel.style.setProperty('--gy', `${((y + 0.5) * 100).toFixed(1)}%`);
      });
      tile.addEventListener('pointerleave', () => {
        panel.classList.remove('is-tilting');
        panel.style.setProperty('--rx', '0deg');
        panel.style.setProperty('--ry', '0deg');
      });
    });
  }
}

/* ------------------------------------------------------------- menus */
// Mega menus open on hover (with a short grace period), on click and from the
// keyboard; the sheet repeats the same groups for tablets and phones.
const menuItems = [...document.querySelectorAll('[data-menu]')];
const closeMenus = (except) =>
  menuItems.forEach((item) => {
    if (item !== except) item.querySelector('.nav-trigger').setAttribute('aria-expanded', 'false');
  });
menuItems.forEach((item) => {
  const trigger = item.querySelector('.nav-trigger');
  const panel = item.querySelector('.mega');
  let timer = 0;
  const open = () => {
    clearTimeout(timer);
    closeMenus(item);
    trigger.setAttribute('aria-expanded', 'true');
  };
  const close = (delay = 0) => {
    clearTimeout(timer);
    timer = setTimeout(() => trigger.setAttribute('aria-expanded', 'false'), delay);
  };
  if (finePointer) {
    item.addEventListener('pointerenter', () => {
      clearTimeout(timer);
      timer = setTimeout(open, 70);
    });
    item.addEventListener('pointerleave', () => close(180));
  }
  trigger.addEventListener('click', () => (trigger.getAttribute('aria-expanded') === 'true' ? close() : open()));
  trigger.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      open();
      panel.querySelector('a')?.focus();
    }
  });
  item.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      close();
      trigger.focus();
    }
  });
  item.addEventListener('focusout', (e) => {
    if (!item.contains(e.relatedTarget)) close(120);
  });
  panel.addEventListener('click', (e) => {
    if (e.target.closest('a')) close();
  });
});
document.addEventListener('pointerdown', (e) => {
  if (!e.target.closest('[data-menu]')) closeMenus();
});

const burger = document.querySelector('[data-burger]');
const sheet = document.querySelector('[data-sheet]');
if (burger && sheet) {
  const body = sheet.querySelector('[data-sheet-body]');
  // the sheet reuses the menus' links and the page's own actions
  menuItems.forEach((item) => {
    const group = document.createElement('section');
    group.className = 'sheet-group';
    const title = document.createElement('h2');
    title.textContent = item.querySelector('.nav-trigger').textContent.trim();
    group.append(title, item.querySelector('.mega-links').cloneNode(true));
    body.append(group);
  });
  const extra = document.createElement('div');
  extra.className = 'sheet-actions';
  const plain = document.querySelector('.nav-plain').cloneNode(true);
  plain.className = 'btn btn-glass';
  const cta = document.querySelector('.nav-actions .btn-primary').cloneNode(true);
  cta.classList.remove('btn-small');
  const login = document.querySelector('.nav-login').cloneNode(true);
  login.className = 'btn btn-glass';
  extra.append(cta, login, plain);
  body.append(extra, document.querySelector('.nav-actions .lang-switch').cloneNode(true));
  const setSheet = (openIt) => {
    burger.setAttribute('aria-expanded', String(openIt));
    sheet.hidden = !openIt;
    html.classList.toggle('no-scroll', openIt);
    if (openIt) sheet.querySelector('[data-sheet-close]').focus();
    else burger.focus();
  };
  burger.addEventListener('click', () => setSheet(true));
  sheet.querySelector('[data-sheet-close]').addEventListener('click', () => setSheet(false));
  // a modal sheet: Esc closes it and Tab stays inside it
  sheet.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      setSheet(false);
      return;
    }
    if (e.key !== 'Tab') return;
    const stops = [...sheet.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.offsetParent !== null);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
  sheet.addEventListener('click', (e) => {
    if (e.target.closest('a')) setSheet(false);
  });
}

/* ---------------------------------------------------- arrows, buttons */
// every "→" becomes a pair of arrows that swap on hover
const arrowSvg = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 10h12.5M11 5l5 5-5 5"/></svg>';
document.querySelectorAll('.arrow').forEach((a) => {
  if (a.querySelector('svg')) return;
  a.innerHTML = arrowSvg + arrowSvg;
});
// the highlight follows the pointer across every metal button
if (finePointer) {
  document.addEventListener('pointermove', (e) => {
    const btn = e.target.closest?.('.btn');
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    btn.style.setProperty('--px', `${e.clientX - r.left}px`);
    btn.style.setProperty('--py', `${e.clientY - r.top}px`);
  }, { passive: true });
}

/* ------------------------------------------------ segmented controls */
document.querySelectorAll('.billing-switch, .device-tabs').forEach(addThumb);
addEventListener('load', () => document.querySelectorAll('.has-thumb').forEach(updateThumb));

/* --------------------------------------------- section in view (nav) */
const navLinks = [...document.querySelectorAll('.nav-plain[href^="#"], .mega-link[href^="#"]')];
const spied = navLinks.map((a) => document.querySelector(a.getAttribute('href'))).filter(Boolean);
if (spied.length) {
  const spy = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        navLinks.forEach((a) => {
          if (a.getAttribute('href') === `#${entry.target.id}`) a.setAttribute('aria-current', 'true');
          else a.removeAttribute('aria-current');
        });
      }
    },
    { rootMargin: '-45% 0px -50% 0px' },
  );
  spied.forEach((section) => spy.observe(section));
}

/* ----------------------------------------------------- magnetic calls */
// the main calls to action lean a few pixels towards the pointer
if (finePointer && !reduced) {
  document.querySelectorAll('[data-magnetic]').forEach((btn) => {
    btn.addEventListener('pointermove', (e) => {
      const r = btn.getBoundingClientRect();
      const x = e.clientX - (r.left + r.width / 2);
      const y = e.clientY - (r.top + r.height / 2);
      btn.style.setProperty('--tx', `${(x * 0.18).toFixed(1)}px`);
      btn.style.setProperty('--ty', `${(y * 0.28).toFixed(1)}px`);
    });
    btn.addEventListener('pointerleave', () => {
      btn.style.setProperty('--tx', '0px');
      btn.style.setProperty('--ty', '0px');
    });
  });
}

addEventListener('load', () => ScrollTrigger.refresh());
